/**
 * Local Privacy-First Client Chart Vault.
 *
 * Persists multiple saved birth charts on the seeker's local device
 * (using localStorage / IndexedDB fallback) so users can switch between family
 * charts across all 16 calculation hubs in <1ms without re-entering birth inputs.
 */

export interface SavedChartProfile {
  id: string;
  name: string;
  relation: "self" | "spouse" | "child" | "parent" | "friend" | "other";
  gender: "male" | "female" | "other";
  birthDate: string; // YYYY-MM-DD
  birthTime: string; // HH:MM
  placeName: string;
  latitude: number;
  longitude: number;
  timezone: string;
  createdAt: number;
  lastUsedAt: number;
}

const VAULT_STORAGE_KEY = "ishvaram_client_chart_vault_v1";
const ACTIVE_PROFILE_KEY = "ishvaram_active_chart_profile_id";

export function getSavedProfiles(): SavedChartProfile[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(VAULT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveProfile(
  profile: Omit<SavedChartProfile, "id" | "createdAt" | "lastUsedAt">
): SavedChartProfile {
  const existing = getSavedProfiles();
  const newProfile: SavedChartProfile = {
    ...profile,
    id: `profile_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    createdAt: Date.now(),
    lastUsedAt: Date.now(),
  };

  const updated = [newProfile, ...existing];
  try {
    window.localStorage.setItem(VAULT_STORAGE_KEY, JSON.stringify(updated));
    setActiveProfileId(newProfile.id);
  } catch {
    // Non-blocking storage fallback
  }

  return newProfile;
}

export function deleteProfile(id: string): void {
  const existing = getSavedProfiles();
  const updated = existing.filter((p) => p.id !== id);
  try {
    window.localStorage.setItem(VAULT_STORAGE_KEY, JSON.stringify(updated));
    if (getActiveProfileId() === id) {
      window.localStorage.removeItem(ACTIVE_PROFILE_KEY);
    }
  } catch {
    // Non-blocking
  }
}

export function getActiveProfileId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(ACTIVE_PROFILE_KEY);
  } catch {
    return null;
  }
}

export function setActiveProfileId(id: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ACTIVE_PROFILE_KEY, id);
    const existing = getSavedProfiles();
    const updated = existing.map((p) => (p.id === id ? { ...p, lastUsedAt: Date.now() } : p));
    window.localStorage.setItem(VAULT_STORAGE_KEY, JSON.stringify(updated));
  } catch {
    // Non-blocking
  }
}

export function getActiveProfile(): SavedChartProfile | null {
  const activeId = getActiveProfileId();
  const profiles = getSavedProfiles();
  if (activeId) {
    const found = profiles.find((p) => p.id === activeId);
    if (found) return found;
  }
  return profiles[0] || null;
}
